import { useCallback, useState } from "react";
import { POS_STORAGE_KEYS, readStored, writeStored } from "../utils/storage";

/**
 * Parked tickets.
 *
 * At peak hours a customer regularly steps aside — to fetch another item or
 * find their wallet — while the next person is served. Holding the ticket
 * keeps the queue moving instead of losing the basket.
 */
export const useHeldTickets = () => {
  const [tickets, setTickets] = useState(() =>
    readStored(POS_STORAGE_KEYS.heldTickets, [])
  );

  const persist = useCallback((next) => {
    setTickets(next);
    writeStored(POS_STORAGE_KEYS.heldTickets, next);
  }, []);

  const hold = useCallback(
    (ticket) => {
      const held = {
        id: `held_${Date.now()}`,
        label:
          ticket.customerLabel?.trim() ||
          ticket.customer?.name ||
          ticket.lines[0]?.product.name ||
          "Ticket",
        lines: ticket.lines,
        discount: ticket.discount,
        customerLabel: ticket.customerLabel,
        // The card scanned for this basket goes with it, and comes back with
        // it — left behind, it would be credited with the next customer's sale
        customer: ticket.customer || null,
        total: ticket.totals.total,
        itemCount: ticket.totals.itemCount,
        heldAt: new Date().toISOString(),
      };

      persist([held, ...readStored(POS_STORAGE_KEYS.heldTickets, [])]);
      return held;
    },
    [persist]
  );

  const release = useCallback(
    (id) => {
      const stored = readStored(POS_STORAGE_KEYS.heldTickets, []);
      const ticket = stored.find((item) => item.id === id);
      persist(stored.filter((item) => item.id !== id));
      return ticket;
    },
    [persist]
  );

  return { tickets, hold, release, count: tickets.length };
};
